// src/utils/messageUtils.js
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";
import { devLog } from "./environment";

/**
 * Start or return an existing chat between two users for a listing
 * Creates a normalized thread ID that's the same regardless of who initiates the conversation
 * @param {string} user1Uid - UID of the first user (could be buyer or seller)
 * @param {string} user2Uid - UID of the second user (could be seller or buyer)
 * @param {string} listingId - Firestore ID of the listing
 * @returns {Promise<string>} chatId of the conversation
 */
export async function startDirect(user1Uid, user2Uid, listingId) {
  // Validate inputs
  if (!user1Uid || !user2Uid || !listingId) {
    throw new Error("Missing required parameters for message thread");
  }

  // Prevent duplicates by making a deterministic ID regardless of who initiates
  // Always put user IDs in alphabetical order to ensure consistency
  const [firstUid, secondUid] = [user1Uid, user2Uid].sort();
  const chatId = `${firstUid}_${secondUid}_${listingId}`;
  const chatRef = doc(db, "messages", chatId);

  try {
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) {
      const chatData = {
        participants: [user1Uid, user2Uid], // Preserve original parameter order for user lookup
        listingId: listingId,
        lastMessage: "",
        lastUpdated: serverTimestamp(),
        createdAt: serverTimestamp(),
      };
      
      await setDoc(chatRef, chatData);

      // Wait a moment and verify creation
      await new Promise(resolve => setTimeout(resolve, 500));
      const verifySnap = await getDoc(chatRef);
      if (!verifySnap.exists()) {
        throw new Error("Failed to create message thread document - verification failed");
      }
      devLog.success("Chat document created successfully");
    } else {
      devLog.info("Chat already exists, using existing");
    }

    return chatId;
  } catch (error) {
    devLog.error("Error in startDirect:", error);
    throw error;
  }
}
